import Link from "next/link";

interface PrivateNumbersFAQProps {
  countryName?: string;
  countryParam?: string;
  freeCount?: number;
  privateCount?: number;
  dialCode?: string;
  capital?: string;
  majorCities?: string[];
}

const PrivateNumbersFAQ: React.FC<PrivateNumbersFAQProps> = ({
  countryName = "",
  countryParam,
  freeCount = 0,
  privateCount = 0,
  dialCode,
  capital,
  majorCities,
}) => {
  const freeUrl = countryParam ? `/free-temporary-numbers/${countryParam}` : "/free-temporary-numbers";
  const hasCountry = countryName.length > 0;

  // Derive non-capital cities for content differentiation
  const otherCities = (majorCities || []).filter(
    (c) => c.toLowerCase() !== (capital || "").toLowerCase()
  );
  const city2 = otherCities[0] || "";

  // Dynamic content helpers
  const privateCountText = privateCount > 0 ? `${privateCount}` : "available";
  const freeCountText = freeCount > 0 ? `${freeCount} free` : "free";
  const dialCodePrefix = dialCode ? `+${dialCode} ` : "";
  const capitalLocationText =
    capital && city2
      ? `${capital}, ${city2}, or anywhere else in the world`
      : capital
        ? `${capital} or anywhere else in the world`
        : "anywhere in the world";
  const capitalText = capital || countryName;

  const faqData = [
    {
      question: `How do I rent a private ${countryName} phone number on SMSS?`,
      answer: (
        <p>
          Renting a private {countryName} phone number with SMSS.BIZ is straightforward. If you&apos;re wondering where can I rent a {countryName} number or how to rent a {countryName} phone number, the answer is right here. Browse the {privateCountText} available {countryName} phone numbers on this page, select the one you want, and complete your rental subscription. Whether you are based in {capitalLocationText}, your dedicated {dialCodePrefix}{countryName} phone number will be immediately assigned to you exclusively and will be ready to receive SMS right away.
        </p>
      ),
      schemaAnswer: `Renting a private ${countryName} phone number with SMSS.BIZ is straightforward. If you're wondering where can I rent a ${countryName} number or how to rent a ${countryName} phone number, the answer is right here. Browse the ${privateCountText} available ${countryName} phone numbers on this page, select the one you want, and complete your rental subscription. Whether you are based in ${capitalLocationText}, your dedicated ${dialCodePrefix}${countryName} phone number will be immediately assigned to you exclusively and will be ready to receive SMS right away.`,
    },
    {
      question: `Why should I rent a private ${countryName} phone number instead of using a free temporary one?`,
      answer: (
        <p>
          A <Link href={freeUrl} className="text-blue-600 hover:underline">free {countryName} temporary phone number</Link> is shared among many users, meaning anyone can see incoming messages, and the number may rotate without notice. A private {countryName} phone number is dedicated exclusively to you, so only you can see your messages. It stays active for the full rental term and works with platforms that block shared or disposable numbers, such as WhatsApp, banking, and dating apps. SMSS.BIZ currently has {freeCountText} and {privateCountText} private {countryName} phone numbers in stock.
        </p>
      ),
      schemaAnswer: `A free ${countryName} temporary phone number is shared among many users, meaning anyone can see incoming messages, and the number may rotate without notice. A private ${countryName} phone number is dedicated exclusively to you, so only you can see your messages. It stays active for the full rental term and works with platforms that block shared or disposable numbers, such as WhatsApp, banking, and dating apps. SMSS.BIZ currently has ${freeCountText} and ${privateCountText} private ${countryName} phone numbers in stock.`,
    },
    {
      question: `Can I use a private ${countryName} phone number for dating apps like Tinder?`,
      answer: (
        <p>
          Yes. A private phone number for dating in {countryName} is a very common use case. Renting a dedicated {countryName} phone number lets you sign up for Tinder, Bumble, Hinge, and other dating apps popular in {capitalText} and across {countryName} without ever giving out your real personal number. When you are done, simply end your rental and the number is permanently deleted.
        </p>
      ),
      schemaAnswer: `Yes. A private phone number for dating in ${countryName} is a very common use case. Renting a dedicated ${countryName} phone number lets you sign up for Tinder, Bumble, Hinge, and other dating apps popular in ${capitalText} and across ${countryName} without ever giving out your real personal number. When you are done, simply end your rental and the number is permanently deleted.`,
    },
    {
      question: `Is a rented ${countryName} phone number VoIP or non-VoIP?`,
      answer: (
        <p>
          Our {countryName} private phone numbers are non-VoIP, meaning they are real, carrier-registered {dialCodePrefix}numbers that work on virtually every platform, including those that block VoIP numbers. This makes them far more reliable for SMS verification, account registration, and business use than standard virtual VoIP lines.
        </p>
      ),
      schemaAnswer: `Our ${countryName} private phone numbers are non-VoIP, meaning they are real, carrier-registered ${dialCodePrefix}numbers that work on virtually every platform, including those that block VoIP numbers. This makes them far more reliable for SMS verification, account registration, and business use than standard virtual VoIP lines.`,
    },
    {
      question: `Can I choose my own private ${countryName} phone number?`,
      answer: (
        <p>
          You can select from all {privateCountText} currently available {countryName} phone numbers shown on this page. While we do not offer full custom number creation, you can browse the listed options and choose your {countryName} virtual phone number from the available inventory, giving you control over which number becomes exclusively yours.
        </p>
      ),
      schemaAnswer: `You can select from all ${privateCountText} currently available ${countryName} phone numbers shown on this page. While we do not offer full custom number creation, you can browse the listed options and choose your ${countryName} virtual phone number from the available inventory, giving you control over which number becomes exclusively yours.`,
    },
    {
      question: `What happens when my ${countryName} phone number rental period ends?`,
      answer: (
        <p>
          When your {countryName} phone number subscription ends, the number and all associated data are immediately and permanently deleted. It cannot be recovered, and it will not be reassigned to another user until it is fully cleared. If you want to keep your number, simply renew your lease before it expires.
        </p>
      ),
      schemaAnswer: `When your ${countryName} phone number subscription ends, the number and all associated data are immediately and permanently deleted. It cannot be recovered, and it will not be reassigned to another user until it is fully cleared. If you want to keep your number, simply renew your lease before it expires.`,
    },
    {
      question: `Can I use one private ${countryName} phone number for multiple accounts?`,
      answer: (
        <p>
          Yes. Many users rent a {countryName} phone number for multiple accounts across different platforms. Because your dedicated {countryName} phone number is exclusive to you, you can use it to manage verifications across several services simultaneously, without any interference from other users.
        </p>
      ),
      schemaAnswer: `Yes. Many users rent a ${countryName} phone number for multiple accounts across different platforms. Because your dedicated ${countryName} phone number is exclusive to you, you can use it to manage verifications across several services simultaneously, without any interference from other users.`,
    },
    {
      question: `Is there a contract or long-term commitment for a private ${countryName} phone number?`,
      answer: (
        <p>
          No. SMSS.BIZ operates on a flexible subscription basis with no long-term contracts required. You can rent a {countryName} phone number online, pay week-to-week or month-to-month, and cancel at any time. For users who want extended access, we also offer longer rental periods at a discounted rate.
        </p>
      ),
      schemaAnswer: `No. SMSS.BIZ operates on a flexible subscription basis with no long-term contracts required. You can rent a ${countryName} phone number online, pay week-to-week or month-to-month, and cancel at any time. For users who want extended access, we also offer longer rental periods at a discounted rate.`,
    },
    {
      question: `Can developers use a private ${countryName} phone number for API and SMS testing?`,
      answer: (
        <p>
          Yes. Developers use our non-VoIP private phone numbers to receive SMS online for API testing, app verification, and QA automation. They are ideal for testing 2FA systems, OTP flows, and mobile app integrations so teams can simulate real-world phone behaviour without exposing personal data.
        </p>
      ),
      schemaAnswer: `Yes. Developers use our non-VoIP private phone numbers to receive SMS online for API testing, app verification, and QA automation. They are ideal for testing 2FA systems, OTP flows, and mobile app integrations so teams can simulate real-world phone behaviour without exposing personal data.`,
    },
    {
      question: `How quickly do SMS arrive on a private ${countryName} phone number?`,
      answer: (
        <p>
          SMS messages typically arrive within seconds of being sent. Because your private {countryName} phone number is a real, carrier-registered {dialCodePrefix}line, delivery times are comparable to a standard mobile phone. Incoming messages appear on your SMSS.BIZ dashboard in real time, with no manual refresh needed.
        </p>
      ),
      schemaAnswer: `SMS messages typically arrive within seconds of being sent. Because your private ${countryName} phone number is a real, carrier-registered ${dialCodePrefix}line, delivery times are comparable to a standard mobile phone. Incoming messages appear on your SMSS.BIZ dashboard in real time, with no manual refresh needed.`,
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.schemaAnswer,
      },
    })),
  };

  return (
    <div className="w-full py-10">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="space-y-6">
        <h2 className="text-3xl font-bold text-gray-800">
          {hasCountry ? `${countryName} Private Phone Number FAQ` : "Private Phone Number (Non-Voip Phone Number) FAQ"}
        </h2>
        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <details key={index} className="border border-gray-200 rounded-lg">
              <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-800 hover:bg-gray-50">
                {faq.question}
              </summary>
              <div className="px-6 pb-4 text-gray-600">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivateNumbersFAQ;
